import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SubscriptionTiers = ({ selectedTier, onTierSelect }) => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const subscriptionPlans = [
    {
      id: 'basic',
      name: 'Basic',
      description: 'Perfect for beginners exploring crypto trading',
      monthlyPrice: 29,
      yearlyPrice: 290,
      features: [
        'Basic AI predictions for top 10 cryptocurrencies',
        'Daily market analysis reports',
        'Email trading signals',
        'Basic portfolio tracking',
        'Educational content library',
        'Community forum access'
      ],
      limitations: [
        'Limited to 5 predictions per day',
        'Basic technical indicators only'
      ],
      popular: false,
      color: 'border-border'
    },
    {
      id: 'premium',
      name: 'Premium',
      description: 'Advanced tools for serious traders',
      monthlyPrice: 79,
      yearlyPrice: 790,
      features: [
        'Advanced AI predictions for 50+ cryptocurrencies',
        'Real-time trading signals and alerts',
        'Advanced portfolio analytics',
        'Risk assessment tools',
        'Technical analysis charts',
        'Priority email support',
        'Mobile app access',
        'Custom watchlists'
      ],
      limitations: [],
      popular: true,
      color: 'border-accent'
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'Complete solution for professional traders',
      monthlyPrice: 149,
      yearlyPrice: 1490,
      features: [
        'Premium AI predictions for 100+ cryptocurrencies',
        'Institutional-grade market analysis',
        'Advanced trading automation tools',
        'Personalized investment strategies',
        'Direct access to trading experts',
        '24/7 priority support',
        'API access for custom integrations',
        'White-label solutions',
        'Tax optimization tools'
      ],
      limitations: [],
      popular: false,
      color: 'border-success'
    }
  ];

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan?.monthlyPrice : plan?.yearlyPrice;
  };

  const getSavings = (plan) => {
    const monthlyCost = plan?.monthlyPrice * 12;
    const yearlyCost = plan?.yearlyPrice;
    return monthlyCost - yearlyCost;
  };

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-semibold text-foreground mb-4">Choose Your Trading Plan</h2>
        <p className="text-muted-foreground mb-6">
          Start with any plan and upgrade anytime as your trading needs grow
        </p>
        
        {/* Billing Toggle */}
        <div className="inline-flex items-center bg-muted rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-standard ${
              billingCycle === 'monthly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-standard ${
              billingCycle === 'yearly' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            Yearly
            <span className="ml-1 text-xs bg-success text-success-foreground px-1.5 py-0.5 rounded">
              Save up to 20%
            </span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {subscriptionPlans?.map((plan) => (
          <div
            key={plan?.id}
            className={`relative bg-card border-2 rounded-lg p-6 transition-all duration-300 hover:shadow-elevation-3 ${
              plan?.color
            } ${
              selectedTier === plan?.id ? 'ring-2 ring-primary ring-offset-2' : ''
            } ${
              plan?.popular ? 'scale-105' : ''
            }`}
          >
            {plan?.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
            )}

            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold text-card-foreground mb-2">{plan?.name}</h3>
              <p className="text-muted-foreground text-sm mb-4">{plan?.description}</p>
              
              <div className="mb-4">
                <span className="text-3xl font-bold text-card-foreground">
                  ${getPrice(plan)}
                </span>
                <span className="text-muted-foreground">
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </span>
                {billingCycle === 'yearly' && (
                  <div className="text-sm text-success font-medium mt-1">
                    Save ${getSavings(plan)} per year
                  </div>
                )}
              </div>

              <Button
                variant={selectedTier === plan?.id ? 'default' : 'outline'}
                size="lg"
                fullWidth
                onClick={() => onTierSelect(plan?.id)}
                className="mb-6"
              >
                {selectedTier === plan?.id ? 'Selected' : 'Select Plan'}
              </Button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-card-foreground mb-3">Features included:</h4>
                <ul className="space-y-2">
                  {plan?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {plan?.limitations?.length > 0 && (
                <div>
                  <h4 className="font-medium text-card-foreground mb-3">Limitations:</h4>
                  <ul className="space-y-2">
                    {plan?.limitations?.map((limitation, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon name="X" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">{limitation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-8 text-center">
        <p className="text-sm text-muted-foreground mb-4">
          All plans include 14-day free trial • Cancel anytime • No setup fees
        </p>
        <div className="flex items-center justify-center space-x-6 text-xs text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Icon name="Shield" size={14} />
            <span>SSL Secured</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="CreditCard" size={14} />
            <span>Secure Payments</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="RefreshCw" size={14} />
            <span>Money Back Guarantee</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTiers;